/**
 * Access levels and feature stages, as the SPA shows them (docs/ACCESS.md).
 *
 * The server keeps its own copy of these words (backend/pleach/services/
 * access.py LEVEL_NAMES and STAGE_NAMES) for its copy and docs; this is the
 * SPA's, for its screens. The owner chose them on 2026-09-29:
 *
 *   level    tester         super_tester   admin
 *   shown    Beta           Early access   Admin
 *
 *   stage    dev             early          beta   live
 *   shown    In development  Early access   Beta   Live
 *
 * Everything here is pure: the nav, the shell's strip and banner, Settings
 * and the admin screens read it, and it is unit tested without React.
 */
import type { components } from '@/api/schema';
import type { Session } from '@/lib/session';

export type Level = components['schemas']['SessionAccessOut']['level'];
export type StoredLevel = components['schemas']['UserLevelIn']['level'];
export type Stage = components['schemas']['SessionFeatureOut']['stage'];
export type SessionFeature = components['schemas']['SessionFeatureOut'];
export type SessionAccess = components['schemas']['SessionAccessOut'];
export type EarlyAccessRequest = components['schemas']['EarlyAccessRequestOut'];

export const LEVEL_NAMES: Record<Level, string> = {
  tester: 'Beta',
  super_tester: 'Early access',
  admin: 'Admin',
};

/** The two levels a person can be given (admin is the flag, not a level). */
export const STORED_LEVELS: StoredLevel[] = ['super_tester', 'tester'];

export const STAGES: Stage[] = ['dev', 'early', 'beta', 'live'];

export const STAGE_NAMES: Record<Stage, string> = {
  dev: 'In development',
  early: 'Early access',
  beta: 'Beta',
  live: 'Live',
};

/** Who can use a section at each stage, in the admin's words. */
export const STAGE_AUDIENCE: Record<Stage, string> = {
  dev: 'Admins only',
  early: 'Early access and admins',
  beta: 'Every tester',
  live: 'Everyone',
};

/** The beta-locked sections (BETA_FEATURES keys), in rail order. */
export const SECTION_KEYS = ['menus', 'prep', 'allergens', 'stock'] as const;
export type SectionKey = (typeof SECTION_KEYS)[number];

/** A section's name as the SPA writes it (the server's "Prep List" is Title
 *  Case; the rail says "Prep lists"). */
export const SECTION_NAMES: Record<SectionKey, string> = {
  menus: 'Menus',
  prep: 'Prep lists',
  allergens: 'Allergens',
  stock: 'Stock',
};

/** What a section covers, where its name alone does not say. */
export const SECTION_SCREENS: Partial<Record<SectionKey, string>> = {
  stock: 'Receiving, Stocktake and the Order list',
};

export function isSectionKey(v: unknown): v is SectionKey {
  return typeof v === 'string' && (SECTION_KEYS as readonly string[]).includes(v);
}

/** A section's shown name: the SPA's own for a known key, else the server's
 *  name in sentence case, else the key. */
export function sectionName(key: string, serverName?: string | null): string {
  if (isSectionKey(key)) return SECTION_NAMES[key];
  const n = (serverName ?? '').trim();
  return n ? n.charAt(0).toUpperCase() + n.slice(1).toLowerCase() : key;
}

/** One section's row in the session, or undefined (signed out, an older
 *  server, or a key the server does not list). */
export function sessionFeature(s: Session | null | undefined, key: string | undefined): SessionFeature | undefined {
  if (!key) return undefined;
  return (s?.features ?? []).find((f) => f.key === key);
}

/** Is the viewer a real admin (not impersonating)? Admin powers, the admin
 *  area and See the app as belong to them. */
export function isRealAdmin(s: Session | null | undefined): boolean {
  return Boolean(s?.user?.is_admin && !s.impersonator);
}

/** The level a real admin is previewing, or null for the admin view (and
 *  for everyone else). */
export function previewLevel(s: Session | null | undefined): StoredLevel | null {
  return isRealAdmin(s) ? (s?.access?.preview_level ?? null) : null;
}

/** The person's open early access request, or null. */
export function openRequest(s: Session | null | undefined): EarlyAccessRequest | null {
  const r = s?.early_access_request;
  return r && r.status === 'open' ? r : null;
}

/**
 * The Access row in Settings: the level that decides what opens here, and
 * a note when it is not the person's own to change.
 * - A real admin reads "Admin" (and what they are previewing, if anything).
 * - Everyone else reads their kitchen's level (the kitchen follows its
 *   owner), with "This kitchen's access is set by its owner, {name}." when
 *   the owner is someone else.
 * Null when the session carries no access (signed out, an older server).
 */
export function accessWords(s: Session | null | undefined): { value: string; note: string | null } | null {
  const a = s?.access;
  if (!a) return null;
  if (isRealAdmin(s)) {
    const p = a.preview_level;
    return { value: 'Admin', note: p ? `You're seeing the app as ${LEVEL_NAMES[p]}.` : null };
  }
  const value = LEVEL_NAMES[a.kitchen_level] ?? LEVEL_NAMES.tester;
  return { value, note: a.set_by ? `This kitchen's access is set by its owner, ${a.set_by}.` : null };
}
